import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import api from '../services/api';
import authService from '../services/auth';
import bankService from '../services/bankService';
import config from '../config';
import TopNavBar from '../components/layout/TopNavBar';
import Footer from '../components/layout/Footer';
import './RegisterPage.css';

/**
 * 루미나 화면 층 신작 — 블랙 `pages/RegisterPage.jsx` 의 로직 그대로, JSX·CSS 만 교체(시안 블록 10 `rg-` 회원가입).
 * WHY: SPEC §2 — 회원가입 설정(표시·필수 필드, SMS 인증 여부), 0.5초 자동 중복확인, 인증번호 흐름, validateForm 순서·문구,
 *      성공 문구·3초 후 로그인 이동, 은행 목록(bankService), USDT 지갑 주소 필드 조건은 원본 훅·핸들러·상태가 그대로 담당한다.
 *      화면은 2열 폼(.rg-cols) + 오른쪽 안내 카드(안내 4줄은 CSS 점)로 그리고, App.js 가 /register 에서 헤더·푸터를 숨기므로
 *      상단 바·푸터를 이 화면 안에서 직접 그린다(계약 §7). 시안의 은행 21개·"사용 가능한 아이디입니다" 문구는 옮기지 않는다(SPEC §3.4).
 */
const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    nickname: '',
    password: '',
    passwordConfirm: '',
    email: '',
    referrer: '',
    name: '',
    phone: '',
    verificationCode: '',
    bankName: '',
    accountHolder: '',
    accountNumber: '',
    usdtAddress: ''
  });
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [smsVerificationEnabled, setSmsVerificationEnabled] = useState(null);
  // [2026-11-01] 서버 칸 규칙(아이디 길이 등) — 안내·검사가 서버와 같은 숫자를 쓴다(화면 숫자 박아 두면 어긋난다)
  const [fieldRules, setFieldRules] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [passwordError, setPasswordError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [nicknameError, setNicknameError] = useState('');
  // [2026-10-16] 아이디 칸에 지금 든 값 — 늦게 돌아온 앞 값의 중복 확인 응답이 고친 아이디에 중복 표시를 다시 채우지 않게 대조한다
  const latestUsernameRef = React.useRef('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [checkingNickname, setCheckingNickname] = useState(false);
  const [registrationSettings, setRegistrationSettings] = useState({
    visibleFields: {
      username: true,
      nickname: true,
      password: true,
      email: true,
      referrer: true,
      name: true,
      phone: true,
      bankName: true,
      accountHolder: true,
      accountNumber: true,
      usdtAddress: false
    },
    requiredFields: {
      username: true,
      nickname: true,
      password: true,
      email: false,
      referrer: true,
      name: true,
      phone: true,
      bankName: true,
      accountHolder: true,
      accountNumber: true,
      usdtAddress: false
    }
  });

  // 은행 목록 상태
  const [availableBanks, setAvailableBanks] = useState([]);

  // 은행 목록 로드
  useEffect(() => {
    const loadBanks = async () => {
      try {
        const banks = await bankService.getActiveBanks();
        setAvailableBanks(banks);
      } catch (error) {
        console.error('은행 목록 로드 실패:', error);
        setAvailableBanks([]);
      }
    };
    loadBanks();
  }, []);

  // 회원가입 설정 로드
  React.useEffect(() => {
    const loadRegistrationSettings = async () => {
      try {
        // 회원가입 설정 로드
        const response = await fetch(`${config.API_URL}/registration-settings`);
        const data = await response.json();

        console.log('Registration settings loaded:', data);

        // 필드 설정 로드
        if (data.visibleFields) {
          setRegistrationSettings({
            visibleFields: data.visibleFields,
            requiredFields: data.requiredFields || {}
          });
          console.log('Settings applied:', {
            visibleFields: data.visibleFields,
            requiredFields: data.requiredFields
          });
        }

        if (data.fieldRules) setFieldRules(data.fieldRules);

        if (data.smsVerification) {
          setSmsVerificationEnabled(data.smsVerification.enabled);
          // SMS 인증이 비활성화된 경우 자동으로 인증 완료 처리
          if (!data.smsVerification.enabled) {
            setIsPhoneVerified(true);
          }
        }
      } catch (error) {
        console.error('회원가입 설정 로드 실패:', error);
        // 에러 시 기본값 설정 (SMS 인증 활성화)
        setSmsVerificationEnabled(true);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadRegistrationSettings();
  }, []);

  // 아이디 중복 체크
  const checkUsernameAvailability = React.useCallback(async (username) => {
    // 입력 중 중복 확인은 서버 최소 길이부터 — 규칙을 못 받았으면 종전 기본 4자
    if (!username || username.length < (fieldRules?.username_min ?? 4)) {
      setUsernameError('');
      return;
    }
    
    setCheckingUsername(true);
    try {
      const response = await fetch(`${config.API_URL}/user/check-username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username })
      });
      
      const data = await response.json();
      // [2026-10-16] 응답을 기다리는 동안 아이디가 바뀌었으면(늦게 온 앞 값 응답) 반영하지 않는다
      if (latestUsernameRef.current !== username) return;
      if (data.exists) {
        setUsernameError('동일 아이디가 있습니다');
      } else {
        setUsernameError('');
      }
    } catch (error) {
      console.error('아이디 중복 체크 실패:', error);
    } finally {
      setCheckingUsername(false);
    }
  }, [fieldRules]);

  // 닉네임 중복 체크
  const checkNicknameAvailability = React.useCallback(async (nickname) => {
    if (!nickname || nickname.length < 2) {
      setNicknameError('');
      return;
    }
    
    setCheckingNickname(true);
    try {
      const response = await fetch(`${config.API_URL}/user/check-nickname`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname })
      });
      
      const data = await response.json();
      if (data.exists) {
        setNicknameError('동일 닉네임이 있습니다');
      } else {
        setNicknameError('');
      }
    } catch (error) {
      console.error('닉네임 중복 체크 실패:', error);
    } finally {
      setCheckingNickname(false);
    }
  }, []);

  // 디바운스 타이머 관리
  const [usernameTimer, setUsernameTimer] = useState(null);
  const [nicknameTimer, setNicknameTimer] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 입력 시 에러 메시지 제거
    if (error) setError('');
    
    // 아이디 중복 체크 (디바운싱 적용)
    if (name === 'username') {
      // [2026-10-16] 값이 바뀌면 앞 값의 중복 표시를 바로 지운다 — 남아 있으면 고친 아이디가 확인 전(0.5초)이나 확인 실패 때 "가입하기"에서 막힌다
      setUsernameError('');
      latestUsernameRef.current = value;
      if (usernameTimer) clearTimeout(usernameTimer);
      const newTimer = setTimeout(() => {
        checkUsernameAvailability(value);
      }, 500);
      setUsernameTimer(newTimer);
    }
    
    // 닉네임 중복 체크 (디바운싱 적용)
    if (name === 'nickname') {
      if (nicknameTimer) clearTimeout(nicknameTimer);
      const newTimer = setTimeout(() => {
        checkNicknameAvailability(value);
      }, 500);
      setNicknameTimer(newTimer);
    }
    
    // 비밀번호 확인 실시간 검증
    if (name === 'passwordConfirm' || (name === 'password' && formData.passwordConfirm)) {
      const password = name === 'password' ? value : formData.password;
      const passwordConfirm = name === 'passwordConfirm' ? value : formData.passwordConfirm;
      
      if (passwordConfirm && password !== passwordConfirm) {
        setPasswordError('비밀번호가 일치하지 않습니다.');
      } else {
        setPasswordError('');
      }
    }
  };

  const handleRequestVerification = async () => {
    if (!formData.phone) {
      setError('휴대폰 번호를 입력해주세요.');
      return;
    }

    try {
      // authService.sendVerificationCode를 통해 실제 SMS 인증번호 발송
      await authService.sendVerificationCode(formData.phone);
      setSuccess('인증번호가 발송되었습니다.');
      setError('');
    } catch (err) {
      // API 실패 시 사용자에게 오류 메시지 표시
      setError(err.message || '인증번호 발송에 실패했습니다.');
      setSuccess('');
    }
  };

  const handleVerifyCode = async () => {
    if (!formData.verificationCode) {
      setError('인증번호를 입력해주세요.');
      return;
    }

    try {
      // authService.verifyCode를 통해 실제 인증번호 검증
      const result = await authService.verifyCode(formData.phone, formData.verificationCode);
      if (result.success) {
        setIsPhoneVerified(true);
        setSuccess('인증이 완료되었습니다.');
        setError('');
      } else {
        setError(result.message || '인증번호가 일치하지 않습니다.');
      }
    } catch (err) {
      // API 실패 시 사용자에게 오류 메시지 표시
      setError(err.message || '인증 확인에 실패했습니다.');
    }
  };

  const validateForm = () => {
    const { requiredFields, visibleFields } = registrationSettings;

    // 동적 필수 필드 검증
    const fieldLabels = {
      username: '아이디',
      nickname: '닉네임',
      password: '비밀번호',
      email: '이메일',
      referrer: '추천인 코드',
      name: '이름',
      phone: '휴대폰 번호',
      bankName: '은행',
      accountHolder: '예금주',
      accountNumber: '계좌번호',
      usdtAddress: 'USDT 지갑 주소',
      realName: '이름',
      referralCode: '추천인 코드'
    };

    // API 필드명과 formData 필드명 매핑
    const fieldMapping = {
      realName: 'name',
      referralCode: 'referrer'
    };

    // 보이는 필드 중 필수인 필드만 검증
    for (const [field, isRequired] of Object.entries(requiredFields)) {
      const formDataField = fieldMapping[field] || field;
      if (isRequired && visibleFields[field] && !formData[formDataField]) {
        setError(`${fieldLabels[field]}을(를) 입력해주세요.`);
        return false;
      }
    }

    // 아이디 검증 (특수문자 불가, 영문+숫자, 길이는 서버 규칙)
    if (visibleFields.username && formData.username) {
      const usernameRegex = /^[a-zA-Z0-9]+$/;
      const usernameMin = fieldRules?.username_min;
      const usernameMax = fieldRules?.username_max;
      if (!usernameRegex.test(formData.username)
        || (usernameMin && formData.username.length < usernameMin)
        || (usernameMax && formData.username.length > usernameMax)) {
        setError(`아이디는 영문/숫자${usernameMin && usernameMax ? ` ${usernameMin}-${usernameMax}자` : ''}로 입력해주세요.`);
        return false;
      }
    }

    // 닉네임 검증 (특수문자 불가, 영문/한글/숫자, 길이는 아래 규칙)
    if (visibleFields.nickname && formData.nickname) {
      const nicknameMin = fieldRules?.nickname_min ?? 2;
      // 최대는 화면 칸 규칙 8자와 서버 최대 중 작은 쪽. 서버 최소(설정 범위 2~20)가 더 크면 그 값으로 맞춰 정규식이 깨지지 않게 한다
      const nicknameMax = Math.max(nicknameMin, Math.min(8, fieldRules?.nickname_max ?? 8));
      const nicknameRegex = new RegExp(`^[a-zA-Z가-힣0-9]{${nicknameMin},${nicknameMax}}$`);
      if (!nicknameRegex.test(formData.nickname)) {
        setError(`닉네임은 영문/한글/숫자 ${nicknameMin}-${nicknameMax}자로 입력해주세요.`);
        return false;
      }
    }

    // 비밀번호 검증 (6-18자)
    if (visibleFields.password && formData.password) {
      if (formData.password.length < 6 || formData.password.length > 18) {
        setError('비밀번호는 6-18자로 입력해주세요.');
        return false;
      }
    }

    // 비밀번호 확인 검증
    if (formData.password !== formData.passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return false;
    }

    // 휴대폰 인증 확인 (SMS 인증이 활성화된 경우만)
    if (smsVerificationEnabled && !isPhoneVerified) {
      setError('휴대폰 인증을 완료해주세요.');
      return false;
    }

    // 예금주와 이름 일치 확인 (둘 다 보이고 값이 있는 경우)
    if (visibleFields.name && visibleFields.accountHolder &&
        formData.name && formData.accountHolder &&
        formData.name !== formData.accountHolder) {
      setError('이름과 예금주명이 일치해야 합니다.');
      return false;
    }

    // [2026-10-16] 아이디 중복 확인 결과("동일 아이디가 있습니다")가 칸 아래에 떠 있으면 제출하지 않는다.
    //   WHY: 종전에는 이 문구가 떠도 "가입하기"가 그대로 서버까지 가서, 서버의 거절 문구로 돌아왔다(서버도 같은 아이디를 거절한다).
    //   이 표시는 아이디를 바꿀 때 바로 지워지고(handleInputChange) 중복 확인 요청이 다시 채운다 — 확인 전·확인 실패면 비어 있어 종전대로 서버가 판정한다.
    //   닉네임 중복은 막지 않는다 — 서버가 닉네임 중복을 거절하지 않으므로(가입 처리는 아이디만 검사) 화면에서만 막으면 새 규칙이 된다. 안내 문구만 종전대로.
    if (usernameError) {
      setError(usernameError);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    console.log('회원가입 요청 데이터:', formData);
    setLoading(true);
    try {
      const result = await register(formData);
      if (result.success) {
        // W90: 완료 문구는 서버가 승인 방식(자동/수동)별로 준다 — 서버 문구가 없을 때만 기본 문구
        setSuccess(result.message || '회원가입이 완료되었습니다. 관리자 승인 후 이용 가능합니다.');
        setError('');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.error || '회원가입에 실패했습니다.');
        setSuccess('');
      }
    } catch (err) {
      setError(err.message || '회원가입에 실패했습니다.');
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  /* [루미나] 필드 한 칸 — 라벨(필수 표시는 회원가입 설정 requiredFields) + 입력 + 상태 문구. 문구·placeholder 는 블랙 원문 */
  const { visibleFields, requiredFields } = registrationSettings;
  /* [2026-09-30] 필수가 아닌 칸은 라벨 옆에 "(선택)" — 승인 시안("이메일 (선택)")과 같게, 회원가입 설정의 필수 여부로 정한다 */
  const req = (name) => (requiredFields[name] ? <span className="req">*</span> : <span className="opt">(선택)</span>);
  /* [2026-09-30] 안내사항 문장은 회원가입 설정(사이트설정 > 회원가입설정 > "유저페이지 회원가입 필드")을 따른다.
     WHY: 블랙 원문 4줄을 그대로 두어, 추천인 코드가 선택인 운영사에서도 "추천인 코드는 필수입니다"가 보였고
          휴대폰 칸을 끈 운영사에서도 "이름, 휴대폰, 예금주 이름이 동일"이 보였다(tosino 실측). 켜진·필수인 칸만 문장에 넣는다. */
  const referrerRequired = !!(visibleFields.referrer && requiredFields.referrer);
  const sameNameFields = [
    visibleFields.name && '이름',
    visibleFields.phone && '휴대폰',
    visibleFields.accountHolder && '예금주',
  ].filter(Boolean);
  const arrowIcon = (
    <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
  );

  return (
    <>
      <TopNavBar />
      <section className="page page-app page-register">
        <div className="container">
          <div className="pg-head">
            <div><span className="eyebrow">REGISTER</span><h2>회원가입</h2></div>
            <div className="acts">
              <button type="button" className="btn-secondary act" onClick={handleLoginClick}>로그인</button>
              <Link className="btn-secondary act" to="/">홈으로</Link>
            </div>
          </div>

          <div className="lk-grid side rg-grid">
            <div className="lk-card">
              {settingsLoading ? (
                <div className="lk-empty">로딩중...</div>
              ) : (
              <form className="lk-form" onSubmit={handleSubmit} noValidate>
                {error && <div className="lk-note danger" role="alert">{error}</div>}
                {success && <div className="lk-note" role="status">{success}</div>}

                <div className="rg-cols">
                  {/* 아이디 입력 */}
                  {visibleFields.username && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_username">아이디{req('username')}</label>
                      <input
                        id="rg_username"
                        type="text"
                        name="username"
                        placeholder={`아이디(특수문자사용불가${fieldRules ? `, ${fieldRules.username_min}~${fieldRules.username_max}자` : ''}, 영문/숫자)`}
                        className="lk-input"
                        value={formData.username}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      {checkingUsername && <span className="rg-status wait">확인중...</span>}
                      {usernameError && <span className="rg-status err">{usernameError}</span>}
                    </div>
                  )}

                  {/* 닉네임 입력 */}
                  {visibleFields.nickname && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_nickname">닉네임{req('nickname')}</label>
                      <input
                        id="rg_nickname"
                        type="text"
                        name="nickname"
                        placeholder={`닉네임(특수문자사용불가, ${fieldRules?.nickname_min ?? 2}~${Math.max(fieldRules?.nickname_min ?? 2, Math.min(8, fieldRules?.nickname_max ?? 8))}자, 영문/한글/숫자)`}
                        className="lk-input"
                        value={formData.nickname}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      {checkingNickname && <span className="rg-status wait">확인중...</span>}
                      {nicknameError && <span className="rg-status err">{nicknameError}</span>}
                    </div>
                  )}

                  {/* 비밀번호 입력 */}
                  {visibleFields.password && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_password">비밀번호{req('password')}</label>
                      <input
                        id="rg_password"
                        type="password"
                        name="password"
                        placeholder="비밀번호(6~18자)"
                        className="lk-input"
                        value={formData.password}
                        onChange={handleInputChange}
                        autoComplete="new-password"
                      />
                    </div>
                  )}

                  {/* 비밀번호 확인 */}
                  {visibleFields.password && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_passwordConfirm">비밀번호 확인{req('password')}</label>
                      <input
                        id="rg_passwordConfirm"
                        type="password"
                        name="passwordConfirm"
                        placeholder="비밀번호 확인"
                        className="lk-input"
                        value={formData.passwordConfirm}
                        onChange={handleInputChange}
                        autoComplete="new-password"
                      />
                      {passwordError && <span className="rg-status err">{passwordError}</span>}
                    </div>
                  )}

                  {/* 이메일 입력 */}
                  {visibleFields.email && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_email">이메일{req('email')}</label>
                      <input
                        id="rg_email"
                        type="email"
                        name="email"
                        placeholder="이메일(비밀번호분실 시 사용)"
                        className="lk-input"
                        value={formData.email}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                    </div>
                  )}

                  {/* 추천인 입력 */}
                  {visibleFields.referrer && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_referrer">추천인 코드{req('referrer')}</label>
                      <input
                        id="rg_referrer"
                        type="text"
                        name="referrer"
                        placeholder="추천인 코드"
                        className="lk-input"
                        value={formData.referrer}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                    </div>
                  )}

                  {/* 이름 입력 */}
                  {visibleFields.name && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_name">이름{req('name')}</label>
                      <input
                        id="rg_name"
                        type="text"
                        name="name"
                        placeholder="이름(실명 미 기재시 환전불가능)"
                        className="lk-input"
                        value={formData.name}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                    </div>
                  )}

                  {/* 휴대폰 번호 입력 (+ SMS 인증 활성 시 인증번호 받기) */}
                  {visibleFields.phone && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_phone">휴대폰 번호{req('phone')}</label>
                      <div className="rg-inline">
                        <input
                          id="rg_phone"
                          type="tel"
                          name="phone"
                          placeholder="휴대폰번호(숫자만입력)"
                          className="lk-input"
                          value={formData.phone}
                          onChange={handleInputChange}
                          autoComplete="off"
                        />
                        {!settingsLoading && smsVerificationEnabled && (
                          <button
                            type="button"
                            className="lk-btn ghost"
                            onClick={handleRequestVerification}
                          >
                            인증번호 받기
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 인증번호 입력 (SMS 인증이 활성화된 경우만 표시) */}
                  {visibleFields.phone && !settingsLoading && smsVerificationEnabled && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_verificationCode">인증번호</label>
                      <div className="rg-inline">
                        <input
                          id="rg_verificationCode"
                          type="text"
                          name="verificationCode"
                          placeholder="인증번호입력"
                          className="lk-input"
                          value={formData.verificationCode}
                          onChange={handleInputChange}
                          autoComplete="off"
                          inputMode="numeric"
                        />
                        <button
                          type="button"
                          className="lk-btn ghost"
                          onClick={handleVerifyCode}
                          disabled={!formData.verificationCode}
                        >
                          확인
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 은행 정보 */}
                  {visibleFields.bankName && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_bankName">은행{req('bankName')}</label>
                      <select
                        id="rg_bankName"
                        name="bankName"
                        className="lk-select"
                        value={formData.bankName}
                        onChange={handleInputChange}
                      >
                        <option value="">은행선택</option>
                        {availableBanks.map((bank) => (
                          <option key={bank.value} value={bank.value}>
                            {bank.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* 예금주 입력 */}
                  {visibleFields.accountHolder && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_accountHolder">예금주{req('accountHolder')}</label>
                      <input
                        id="rg_accountHolder"
                        type="text"
                        name="accountHolder"
                        placeholder="예금주(이름과 일치하여야 함)"
                        className="lk-input"
                        value={formData.accountHolder}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                    </div>
                  )}

                  {/* 계좌번호 입력 */}
                  {visibleFields.accountNumber && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_accountNumber">계좌번호{req('accountNumber')}</label>
                      <input
                        id="rg_accountNumber"
                        type="text"
                        name="accountNumber"
                        placeholder="계좌번호(숫자만입력)"
                        className="lk-input"
                        value={formData.accountNumber}
                        onChange={handleInputChange}
                        autoComplete="off"
                        inputMode="numeric"
                      />
                    </div>
                  )}

                  {/* USDT 지갑 주소 — 회원가입 설정에서 표시로 켠 경우만 */}
                  {visibleFields.usdtAddress && (
                    <div className="lk-field">
                      <label className="lk-label" htmlFor="rg_usdtAddress">USDT 지갑 주소{req('usdtAddress')}</label>
                      <input
                        id="rg_usdtAddress"
                        type="text"
                        name="usdtAddress"
                        placeholder="USDT 지갑 주소"
                        className="lk-input"
                        value={formData.usdtAddress}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                    </div>
                  )}
                </div>

                {/* 가입하기/로그인 버튼 */}
                <div className="rg-btns">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    <span>{loading ? '가입 중...' : '가입하기'}</span>
                    {arrowIcon}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleLoginClick}
                  >
                    로그인
                  </button>
                </div>
              </form>
              )}
            </div>

            {/* 안내사항 — 블랙 4줄 원문(설정에 맞는 줄만), '-' 접두는 CSS 점으로.
                [2026-09-30] 승인 시안대로 로고를 빼고(상단 바에 이미 있음) 미성년자 경고를 붙였다 */}
            <aside className="rg-side">
              <div className="lk-card soft">
                <div className="lk-card-h"><h3>안내사항</h3></div>
                <div className="lk-note">
                  <p>필수 항목은 모두 입력해 주셔야 합니다.</p>
                  {referrerRequired && <p>추천인 코드는 필수입니다. 추천인에게 받은 코드를 입력하세요.</p>}
                  <p>정확한 정보를 입력하여야 가입이 가능합니다.</p>
                  {sameNameFields.length >= 2 && <p>{sameNameFields.join(', ')} 이름이 동일하여야 가입이 가능합니다.</p>}
                </div>
                <p className="lg-warn">※ 미성년자 이용 절대 불가!</p>
              </div>
            </aside>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default RegisterPage;
